import { useLayout } from 'hooks/useLayout'
import { object, boolean } from 'yup'
import { useEffect, useCallback } from 'react'
import { Modal } from 'components/Modal/Modal'
import { useSnackbar } from 'stores/useSnackbar'
import { yupResolver } from '@hookform/resolvers/yup'
import { Controller, FormProvider, useForm } from 'react-hook-form'
import type { components } from 'api/playerPayback/api'
import { Box, Button, Stack, Switch, Typography, useTheme } from '@mui/material'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { daysOfWeek } from 'utils/util'
import { type ContestEntryBonusSchedule } from 'types/api'
import { ActivityButton } from 'components/ActivityButton'
import { ScheduleRow } from './ScheduleRow'

interface GrantEntriesForm {
  enablePatronContestEntryBonus: boolean
  schedules?: ContestEntryBonusSchedule[]
}

const GrantEntriesSchema = object({
  enablePatronContestEntryBonus: boolean().required(
    'Enable Entry Multiplier is required.'
  ),
})

export const SettingsModal = ({
  sweepstake,
  isModalVisible,
  toggleModalVisibility,
  daysToDisplay = [],
}: {
  sweepstake: components['schemas']['FullContestDTO']
  isModalVisible: boolean
  toggleModalVisibility: () => void
  daysToDisplay: ContestEntryBonusSchedule[]
}) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '75%'
  const setMessage = useSnackbar((state) => state.setMessage)
  const queryClient = useQueryClient()
  const theme = useTheme()

  const formMethods = useForm<GrantEntriesForm>({
    resolver: yupResolver(GrantEntriesSchema),
  })

  const {
    handleSubmit,
    // formState: { errors },
    watch,
    reset,
  } = formMethods

  const resetForm = useCallback(() => {
    reset({
      enablePatronContestEntryBonus: Boolean(
        sweepstake?.contestEntryBonusSchedules?.some(
          (sweepstake) =>
            sweepstake.patronContestEntryBonusMultiplier !== undefined
        )
      ),
    })
  }, [sweepstake])

  useEffect(() => {
    resetForm()
  }, [resetForm])

  const enablePatronContestEntryBonus = watch('enablePatronContestEntryBonus')

  const closeModal = () => {
    toggleModalVisibility()
    resetForm()
  }

  // apply settings to all contests in the group
  const putContestEntryScheduleForGroup = useTypedApiClient({
    path: '/admin/contests/{id}/updateLocationSweepstakeGroup',
    method: 'put',
  })

  const putContestEntrySchedule = useTypedApiClient({
    path: '/admin/contests/{id}',
    method: 'put',
  })

  const updateContestEntryScheduleMutation = useMutation({
    mutationFn: async ({
      enablePatronContestEntryBonus,
      schedules,
    }: GrantEntriesForm) => {
      const contestEntryBonusSchedules: ContestEntryBonusSchedule[] = []
      schedules?.forEach((schedule) => {
        if (
          schedule.opening &&
          schedule.closing &&
          enablePatronContestEntryBonus
        ) {
          contestEntryBonusSchedules.push({
            ...schedule,
            patronContestEntryBonusMultiplier: 2,
          })
        }
      })

      if (sweepstake?.id) {
        sweepstake.enablePatronContestEntryBonus = enablePatronContestEntryBonus
        sweepstake.contestEntryBonusSchedules = contestEntryBonusSchedules

        return sweepstake.groupId
          ? await putContestEntryScheduleForGroup(sweepstake)
          : await putContestEntrySchedule(sweepstake)
      }
    },
  })

  useEffect(() => {
    if (updateContestEntryScheduleMutation.isSuccess) {
      queryClient
        .invalidateQueries({
          queryKey: ['/admin/contests', sweepstake.id],
        })
        .finally(() => {})
      setMessage('Contest Entry Schedule has been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      closeModal()
    } else {
      const error: any = updateContestEntryScheduleMutation.error as Error
      if (error?.message) {
        setMessage(error?.message, 'error', {
          vertical: 'top',
          horizontal: 'right',
        })
      }
    }
  }, [
    updateContestEntryScheduleMutation.isSuccess,
    updateContestEntryScheduleMutation.isError,
  ])

  const updateContestEntrySchedule = handleSubmit((data) => {
    updateContestEntryScheduleMutation.mutate(data)
  })

  return (
    <Modal
      isOpen={isModalVisible}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '70%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={updateContestEntrySchedule}>
          <ModalFullHeader
            title="Grant Additional Sweepstake Entries"
            subTitle={sweepstake.name}
            toggleIsOpen={closeModal}
          />
          <ModalFullBody heightModal={heightModal}>
            <Stack spacing={1}>
              {/* Entry Multiplier Section */}
              {
                <Box
                  sx={{
                    border: 1,
                    borderColor: theme.palette.grey[300],
                    m: '16px',
                    gap: '16px',
                    p: '16px',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      gap: '4px',
                      flexDirection: 'column',
                    }}
                  >
                    <Typography variant="h4">Entry Multiplier</Typography>
                    <Typography variant="body-3">
                      When enabled the user will receive 2x Sweepstake entries
                      per check in.
                    </Typography>
                  </Box>
                  <Controller
                    name="enablePatronContestEntryBonus"
                    render={({ field: { onChange, value } }) => {
                      return <Switch checked={value} onChange={onChange} />
                    }}
                  />
                </Box>
              }
              {enablePatronContestEntryBonus && (
                <Box
                  sx={{
                    padding: '16px',
                  }}
                >
                  <Typography variant="subtitle-1" component={'p'}>
                    Sweepstake Entry Multiplier Schedule
                  </Typography>
                  <Typography variant="body-3">
                    {`Select the days you'd like to award 2x entries`}
                  </Typography>
                  <Stack spacing={2} mt="20px">
                    {daysOfWeek.map((day, index) => {
                      return (
                        <Box
                          key={day}
                          style={{ height: '85px' }}
                          height={'85'}
                          className="flex flex-col justify-center"
                        >
                          <ScheduleRow
                            day={day}
                            index={index}
                            key={day}
                            defaultValues={{
                              opening: daysToDisplay?.[index]?.opening,
                              closing: daysToDisplay?.[index]?.closing,
                              patronContestEntryBonusMultiplier:
                                daysToDisplay?.[index]
                                  ?.patronContestEntryBonusMultiplier ?? 0,
                            }}
                            openLabel="Enabled"
                            closedLabel="Disabled"
                          />
                        </Box>
                      )
                    })}
                  </Stack>
                </Box>
              )}
            </Stack>
          </ModalFullBody>
          <ModalFullFooter>
            <Button variant="text" onClick={closeModal}>
              Cancel
            </Button>
            <ActivityButton
              active={updateContestEntryScheduleMutation.isPending}
              variant="contained"
              type="submit"
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}
